/**
 * Sidebar.jsx — PrimeCompliance Sidebar Navigation
 * Dark theme, collapsible, hover-based popout sub-menus.
 * Adapted for React Router v5 + Redux integration.
 */

import React, { useState, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { isLocalhost } from "../../util/helpers";
import { sidebarNav, productLinks } from "./navigationConfig";
import "./Sidebar.css";

// ── SVG Icon Map ──
const icons = {
  building: (
    <svg viewBox="0 0 24 24">
      <rect x="4" y="2" width="16" height="20" rx="1.5" />
      <line x1="9" y1="6" x2="9" y2="6.01" />
      <line x1="15" y1="6" x2="15" y2="6.01" />
      <line x1="9" y1="10" x2="9" y2="10.01" />
      <line x1="15" y1="10" x2="15" y2="10.01" />
      <line x1="9" y1="14" x2="9" y2="14.01" />
      <line x1="15" y1="14" x2="15" y2="14.01" />
      <rect x="10" y="18" width="4" height="4" />
    </svg>
  ),
  file: (
    <svg viewBox="0 0 24 24">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
    </svg>
  ),
  monitor: (
    <svg viewBox="0 0 24 24">
      <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
      <line x1="8" y1="21" x2="16" y2="21" />
      <line x1="12" y1="17" x2="12" y2="21" />
    </svg>
  ),
  chevronLeft: (
    <svg viewBox="0 0 24 24">
      <polyline points="15 18 9 12 15 6" />
    </svg>
  ),
  chevronRight: (
    <svg viewBox="0 0 24 24">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  ),
};

// ── Nav Item (with optional popout) ──
function NavItem({ item, isCollapsed, pathname, onNavigate }) {
  const [isPopoutOpen, setPopoutOpen] = useState(false);
  const openTimer = useRef(null);
  const closeTimer = useRef(null);
  const popoutRef = useRef(null);

  const OPEN_DELAY = 120;
  const CLOSE_DELAY = 250;

  // Determine if this item or any of its children is active
  const isActive = item.route
    ? ["/", item.route].includes(pathname) &&
      (item.key === "company"
        ? ["/", "/CompanyMenuDetails"].includes(pathname)
        : pathname === item.route)
    : item.popout &&
      item.popout.some(
        (sub) =>
          pathname === sub.route ||
          (sub.route === "/MonitorLanding" &&
            pathname.includes("/MonitorLanding"))
      );

  const hasPopout = !!item.popout;

  const handleMouseEnter = useCallback(() => {
    if (!hasPopout) return;
    clearTimeout(closeTimer.current);
    openTimer.current = setTimeout(() => {
      setPopoutOpen(true);
      if (popoutRef.current) {
        popoutRef.current.style.top = "0";
        const rect = popoutRef.current.getBoundingClientRect();
        if (rect.bottom > window.innerHeight - 10) {
          popoutRef.current.style.top = `${-(
            rect.bottom -
            window.innerHeight +
            10
          )}px`;
        }
      }
    }, OPEN_DELAY);
  }, [hasPopout]);

  const handleMouseLeave = useCallback(() => {
    clearTimeout(openTimer.current);
    closeTimer.current = setTimeout(
      () => setPopoutOpen(false),
      CLOSE_DELAY
    );
  }, []);

  const handleClick = () => {
    if (!hasPopout && item.route) {
      onNavigate(item.route);
    }
  };

  return (
    <div
      className={`navItem ${isActive ? "active" : ""} ${
        hasPopout ? "hasPopout" : ""
      } ${isPopoutOpen ? "popoutOpen" : ""}`}
      data-page={item.key}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button
        className="navItemBtn"
        data-tooltip={item.label}
        onClick={handleClick}
      >
        <span className="navIcon">{icons[item.icon]}</span>
        <span className="label">{item.label}</span>
        {hasPopout && (
          <span className="chevron">{icons.chevronRight}</span>
        )}
      </button>
      {hasPopout && (
        <div className="popout" ref={popoutRef}>
          <div className="popoutHeader">{item.label}</div>
          {item.popout.map((subItem, i) => {
            const isSubActive =
              pathname === subItem.route ||
              (subItem.route === "/MonitorLanding" &&
                pathname.includes("/MonitorLanding"));
            return (
              <a
                key={i}
                className={`popoutItem ${isSubActive ? "active" : ""}`}
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  onNavigate(subItem.route);
                  setPopoutOpen(false);
                }}
              >
                <span className="dot" />
                {subItem.label}
              </a>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Main Sidebar Component ──
export default function Sidebar({ collapsed, onToggle }) {
  const history = useHistory();
  const { pathname } = useLocation();
  const loggedUserInfo = useSelector((state) => state.loggedUserInfo);

  const hasFullAccess =
    loggedUserInfo &&
    loggedUserInfo.items &&
    !loggedUserInfo.items.ExcludeDeparmentalActions;

  const clientInitial =
    loggedUserInfo &&
    loggedUserInfo.items &&
    loggedUserInfo.items.ClientName
      ? loggedUserInfo.items.ClientName.charAt(0).toUpperCase()
      : "P";

  const clientId =
    loggedUserInfo &&
    loggedUserInfo.items &&
    loggedUserInfo.items.ClientID
      ? loggedUserInfo.items.ClientID
      : "";

  const kycEnvironment = isLocalhost()
    ? "http://kyc.localhost.com:3010"
    : process.env.REACT_APP_NEW_HOST;

  const handleNavigate = (route) => {
    history.push(route);
  };

  // Filter nav items based on permissions
  const visibleItems = sidebarNav.items.filter(
    (item) => !item.requiresFullAccess || hasFullAccess
  );

  return (
    <nav className={`sidebar ${collapsed ? "collapsed" : ""}`}>
      {/* Collapse Toggle */}
      <button
        className="sidebarToggle"
        onClick={onToggle}
        title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
      >
        {icons.chevronLeft}
      </button>

      {/* Brand */}
      <div className="sidebarBrand">
        <div className="brandGrid">
          <span />
          <span />
          <span />
          <span />
        </div>
        <div className="brandText">
          <span className="prime">PRIME</span>
          <span className="compliance">COMPLIANCE</span>
        </div>
      </div>

      {/* Client Selector */}
      <div className="clientSelector">
        <div className="clientAvatar">{clientInitial}</div>
        <span className="clientName">{clientId}</span>
      </div>

      {/* Navigation */}
      <div className="sidebarNav">
        <div className="sectionLabel">{sidebarNav.sectionLabel}</div>
        {visibleItems.map((item) => (
          <NavItem
            key={item.key}
            item={item}
            isCollapsed={collapsed}
            pathname={pathname}
            onNavigate={handleNavigate}
          />
        ))}
      </div>

      {/* Product Links */}
      <div className="productLinks">
        {/* Prime Documents */}
        <div className="productLink documents">
          <div className="productTooltip">
            Manage and store your company documents securely.
          </div>
          <img
            src="/prime-docs-logo.svg"
            alt="Prime Documents"
            className="productLogo"
          />
        </div>

        {/* Prime KYC – external link */}
        <a
          className="productLink search"
          href={`${kycEnvironment}/#/know-your-customer`}
          target="_blank"
          rel="noreferrer"
        >
          <div className="productTooltip">
            Know Your Customer – identity verification and compliance.
          </div>
          <img
            src="/prime-kyc-logo.svg"
            alt="Prime KYC"
            className="productLogo"
            style={{ maxWidth: '7rem' }}
          />
        </a>
      </div>
    </nav>
  );
}
